/**
 * OnboardingTour · v8.5.64 · piel — de modal centrado con overlay oscuro +
 * header en degradado por paso, a tooltip de spotlight: caja de 320px sobre
 * --raised, el elemento real señalado con un anillo de 2px --ink (no una
 * máscara recortando el resto de la pantalla — de hecho ya no hay ningún
 * backdrop, la pantalla de atrás queda intacta y usable).
 *
 * Los pasos ahora apuntan a elementos reales del nav (5 zonas, restructurado
 * en v8.5.46) en vez de narrar conceptos que ya no existen como pestañas
 * propias (Pedidos vive dentro de Trabajo, no es un paso separado) — el
 * paso "hablanos por audio" señala el VoiceMemoFAB real (el único punto de
 * entrada de audio que persiste en todo el portal), con `data-tour="voice-fab"`
 * agregado ahí solo para este propósito, sin cambiar su piel.
 *
 * Bienvenida y cierre no señalan nada (no hay un elemento único al que
 * apuntar) y se centran en pantalla, igual sin backdrop.
 */
import { ignorarAtajo } from "../lib/teclado.js";
import { useState, useEffect, useRef, useCallback } from "react";
import { X, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "../lib/api.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useInterruptSlot } from "../context/InterruptBudget.jsx";

const PASOS = [
  {
    titulo: "Bienvenido a tu portal",
    body: "Este es el lugar donde ves todo lo que tu equipo está haciendo por tu marca. Es un tour de 30 segundos.",
  },
  {
    titulo: "Tus piezas",
    body: "Acá aparece todo lo que el equipo prepara para vos. Cuando algo esté listo para tu decisión, lo vas a ver marcado.",
    target: '[data-tour="nav-trabajo"]',
  },
  {
    titulo: "Tu calendario",
    body: "El plan de contenido del mes y las reuniones con el equipo, todo en un solo lugar.",
    target: '[data-tour="nav-plan"]',
  },
  {
    titulo: "Tus reportes",
    body: "Cada mes te llega un reporte con la narrativa completa: qué se publicó y qué funcionó mejor.",
    target: '[data-tour="nav-resultados"]',
  },
  {
    titulo: "Hablanos",
    body: "Este botón graba un audio y lo convierte en un pedido para el equipo — la forma más rápida de pedir algo.",
    target: '[data-tour="voice-fab"]',
  },
  {
    titulo: "Ya sabés moverte",
    body: "Si te perdés, Cmd+K (Ctrl+K en Windows) busca cualquier cosa del portal.",
    cta: "Empezar",
  },
];

export default function OnboardingTour() {
  const { user, clienteActivoId } = useAuth();
  const [mostrar, setMostrar] = useState(false);
  const [pasoIdx, setPasoIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const { granted, request, release } = useInterruptSlot("onboarding", 3);

  useEffect(() => {
    if (!user || !clienteActivoId) return;
    const localKey = `onboarding_completado_${user.id}`;
    if (localStorage.getItem(localKey) === "true") return;

    api.onboardingStatus()
      .then(r => {
        if (r.completado) {
          localStorage.setItem(localKey, "true");
        } else {
          setTimeout(() => { setMostrar(true); request(); }, 1200);
        }
      })
      .catch(() => {});
  }, [user, clienteActivoId]);

  // La bienvenida nueva (BienvenidaPortal) reemplaza a este tour: si se completó en esta sesión, no se muestra.
  useEffect(() => {
    const alCompletar = () => { setMostrar(false); release(); if (user?.id) { try { localStorage.setItem(`onboarding_completado_${user.id}`, "true"); } catch { /* sin storage */ } } };
    window.addEventListener("portal-onboarding-completado", alCompletar);
    return () => window.removeEventListener("portal-onboarding-completado", alCompletar);
  }, [user, release]);

  const completar = useCallback(async () => {
    setMostrar(false);
    release();
    try {
      await api.onboardingComplete();
      if (user?.id) localStorage.setItem(`onboarding_completado_${user.id}`, "true");
    } catch {}
  }, [user, release]);

  const siguiente = useCallback(() => {
    setPasoIdx(i => {
      if (i < PASOS.length - 1) return i + 1;
      completar();
      return i;
    });
  }, [completar]);

  const anterior = useCallback(() => setPasoIdx(i => Math.max(i - 1, 0)), []);

  // Ubicar el elemento real del paso actual — recalcula en cada paso y en
  // resize (el sidebar puede colapsar/expandir, el bottom nav mobile
  // aparece/desaparece según viewport).
  useEffect(() => {
    if (!mostrar || !granted) return;
    const actualizar = () => {
      const sel = PASOS[pasoIdx].target;
      if (!sel) { setRect(null); return; }
      const el = document.querySelector(sel);
      setRect(el ? el.getBoundingClientRect() : null);
    };
    actualizar();
    window.addEventListener("resize", actualizar);
    return () => window.removeEventListener("resize", actualizar);
  }, [mostrar, granted, pasoIdx]);

  useEffect(() => {
    if (!mostrar || !granted) return;
    const handler = (e) => {
      if (ignorarAtajo(e)) return;
      if (e.key === "ArrowRight" || e.key === "Enter") siguiente();
      else if (e.key === "ArrowLeft") anterior();
      else if (e.key === "Escape") completar();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [mostrar, granted, siguiente, anterior, completar]);

  if (!mostrar || !granted) return null;

  const paso = PASOS[pasoIdx];
  const esUltimo = pasoIdx === PASOS.length - 1;

  // Posición de la caja: al lado del elemento señalado (derecha si entra,
  // si no debajo), o centrada en pantalla si el paso no señala nada (o el
  // elemento no está en el DOM — ej. sidebar mobile oculto).
  // v8.5.68 · fix auditoría móvil · CAJA_H_EST clampea el "top" contra el
  // borde inferior real del viewport — antes, para targets del bottom nav
  // (siempre cerca del borde inferior en mobile), "debajo" se calculaba sin
  // límite y la caja completa (incluido "Siguiente") quedaba fuera de
  // pantalla, sin forma de avanzar el tour.
  const CAJA_W = 320, CAJA_H_EST = 260, GAP = 14;
  let cajaStyle;
  if (rect) {
    const entraDerecha = rect.right + GAP + CAJA_W < window.innerWidth - 12;
    if (entraDerecha) {
      cajaStyle = { position: "fixed", left: rect.right + GAP, top: Math.max(12, Math.min(rect.top, window.innerHeight - CAJA_H_EST - 12)) };
    } else {
      const left = Math.max(12, Math.min(rect.left, window.innerWidth - CAJA_W - 12));
      const cabeAbajo = rect.bottom + GAP + CAJA_H_EST < window.innerHeight - 12;
      const top = cabeAbajo ? rect.bottom + GAP : Math.max(12, rect.top - GAP - CAJA_H_EST);
      cajaStyle = { position: "fixed", left, top };
    }
  } else {
    cajaStyle = { position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)" };
  }

  return (
    <>
      {rect && (
        <div
          className="fixed z-[var(--z-modal)] rounded-[20px] border-2 border-[var(--ink)] pointer-events-none transition-all duration-200"
          style={{ left: rect.left - 6, top: rect.top - 6, width: rect.width + 12, height: rect.height + 12 }}
        />
      )}
      <div
        className="shadow-overlay z-[var(--z-modal)] w-[320px] bg-[var(--raised)] rounded-[20px] p-4 transition-all duration-200"
        style={cajaStyle}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <span className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)]">
            {pasoIdx + 1} DE {PASOS.length}
          </span>
          <button onClick={completar} aria-label="Cerrar tour" className="text-[var(--faint)] hover:text-[var(--ink-2)] p-2.5 -m-1.5">
            <X className="h-3.5 w-3.5"/>
          </button>
        </div>
        <h3 className="text-[16px] font-semibold text-[var(--ink)] mb-1.5">{paso.titulo}</h3>
        <p className="text-[15px] text-[var(--ink-2)] leading-relaxed">{paso.body}</p>

        <div className="flex items-center justify-between mt-4">
          {pasoIdx > 0 ? (
            <button onClick={anterior} className="text-[13px] text-[var(--muted)] hover:text-[var(--ink-2)] flex items-center gap-1">
              <ArrowLeft className="h-3 w-3"/> Atrás
            </button>
          ) : <span/>}
          <button onClick={siguiente}
                  className="bg-[var(--ink)] text-[var(--surface)] px-3.5 py-1.5 rounded-full text-[13px] font-medium flex items-center gap-1.5 hover:opacity-90">
            {paso.cta || (esUltimo ? "Listo" : "Siguiente")}
            {!esUltimo && <ArrowRight className="h-3.5 w-3.5"/>}
          </button>
        </div>
      </div>
    </>
  );
}
